import { runInAction } from 'mobx'
import rootStore from '../..' // Import the root store
import { Workflow } from '../../../cls/modules/workflows'
import { getFullItm } from '../../../api/utils/api-item'

import { setWorkflow } from '../../../api/modules/api-workflows'
import { warnPt } from '../../../config'
import { responseHasError } from '../../users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
import { actSaveEntityForDemo } from '../../monthly-reports/actions'

const workflowsStore = rootStore.workflows

/**
 * @async
 * @function newWorkflow
 * @description Creates a new workflow item with the specified ID and marks it as new.
 * @param {string} newId - The ID of the new workflow item.
 * @returns {Promise<void>}
 */
export const newWorkflow = async (newId) => {
	warnPt(newId)
	runInAction(() => {
		const _storeItm = requireWorkflowById(newId)
		_storeItm.setNewFlag()
	})
}

/**
 * @function requireWorkflowById
 * @description Retrieves a workflow item by its ID from the store. If it doesn't exist, creates a new workflow item.
 * @param {string} workflowId - The ID of the workflow item to retrieve.
 * @returns {Workflow} The retrieved or newly created workflow item.
 */
export const requireWorkflowById = (workflowId) => {
	warnPt(workflowId)
	let workflow = workflowsStore.workflows[workflowId]
	if (workflow) {
		return workflow
	}
	runInAction(() => {
		workflowsStore.workflows[workflowId] = new Workflow(workflowId)
	})
	return workflowsStore.workflows[workflowId]
}

/**
 * @async
 * @function actLoadWorkflow
 * @description Loads a workflow item from the server by its ID and updates the store.
 * @param {string} workflowId - The ID of the workflow item to load.
 * @returns {Promise<void>}
 */
export const actLoadWorkflow = wrapWithLoading(
	async (workflowId) => {
		const _response = await getFullItm('workflows', {
			id: workflowId
		})

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				const _storeItm = requireWorkflowById(workflowId)
				_storeItm.updateFromServer(_response.data || {})
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Workflow' },
	true // rethrow errors
)

/**
 * @async
 * @function actSaveWorkflow
 * @description Saves a workflow item to the server.
 * @param {string} workflowId - The ID of the workflow item to save.
 * @param {Object} full - The full data of the workflow item to save.
 * @returns {Promise<boolean>} True if the save was successful.
 */
export const actSaveWorkflow = wrapWithLoading(
	async (workflowId, full, demo = false) => {
		if (demo) {
			actSaveEntityForDemo(
				workflowId,
				full,
				requireWorkflowById,
				rootStore.uiLstWorkflows
			)
			return true
		}

		const _response = await setWorkflow(workflowId, full)

		const { data } = _response
		const hasError = data ? responseHasError(data) : true
		if (hasError) {
			throw data
		} else {
			const _storeItm = requireWorkflowById(workflowId)
			_storeItm.setModified(data.modified)
		}

		return true
	},
	{ loadingMessage: 'Saving Workflow' }
)
